import { useEffect, useRef, useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { WHATSAPP_NUMBER_E164, shortBookingReference } from "@majourneys/shared";
import { useDisplayPriceParts } from "../lib/currency";
import { useRecentBooking, clearRecentBooking } from "../lib/recent-booking";
import { useBookingDraft, clearBookingDraft } from "../lib/booking-draft";
import { bookingsApi } from "../api/bookings.api";
import { useBookingSkin } from "../skin";
import { PW, pewterButton, pewterPanel } from "../internal-ui/pewter-shell";
import { BEVEL_INSET_WHITE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";
import { BookingDetailsSheet } from "./booking-details-sheet";
import { sheetStepOrder, sheetStepPosition } from "../lib/sheet-steps";
import { useFloatingBarRoom } from "../hooks/use-floating-bar-room";

import { Icon } from "@majourneys/icons";

/**
 * A control at rest: white over one navy hairline, never the band (owner,
 * 2026-09-05). The band belongs to the strip itself, which is a thing you read.
 */
const CONTROL_REST = pewterButton;

/**
 * The two CTAs on the status strip, in soft gradients (owner, 2026-09-10:
 * "those two button in soft gradient"). Flat fills read as stickers next to
 * the pewter panel they sit on; a shallow one gives the same lift the strip's
 * own surfaces have without turning either button into a second headline.
 *
 * WhatsApp keeps brand green as its midpoint, so the button is still
 * recognisably WhatsApp — only the top and bottom stops move.
 */
const WHATSAPP_SOFT = {
  background: "linear-gradient(160deg, #45DD8B 0%, #25D366 55%, #1DB458 100%)",
  boxShadow: "0 8px 18px rgba(37,211,102,0.35), inset 0 1px 0 rgba(255,255,255,0.28)",
} as const;

/** The quiet twin: white at the top, a breath of pewter at the bottom. */
const CONTROL_SOFT = {
  background: BEVEL_WHITE_FACE,
  boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${PW.controlEdge}, 0 6px 14px rgba(17,14,57,0.07)`,
} as const;
const bookingKeys = {
  all: ["bookings"] as const,
  detail: (id: string, lang?: string) =>
    ["bookings", "detail", id, lang ?? "en"] as const,
};

const WA_NUMBER = WHATSAPP_NUMBER_E164.replace(/^\+/, "");

interface BookingBarProps {
  title: string;
  cityName: string;
  price: number;
  /** Restaurant table: the bar names the venue instead of quoting a price. */
  isTableFlow?: boolean;
  /** Priced per night (stays, camps): the date step is "When are you staying?". */
  isStayFlow?: boolean;
  typePriceKey: string;
  /** Pre-resolved unit noun that wins over `typePriceKey` — lets per-group
   * activities show their real unit ("quad") instead of "group". */
  typePriceLabel?: string;
  bookHref: string;
  thumbnail?: string;
  observeRef?: RefObject<HTMLElement>;
  /** Activity slug — required to look up any recent booking the user already
   * made for this activity in this browser. */
  slug?: string;
  /** Locale prefix used to build the "View booking" link when a recent
   * booking exists. */
  lang?: string;
  /** When provided, the mobile "Book now" CTA fires this callback (to open
   * an in-page bottom-sheet) instead of navigating. Desktop top-bar always
   * navigates via `bookHref`. */
  onBookSheet?: () => void;
  /** Replaces "Book now" (a women-only trip: "Reserve my seat", or the waiting list). */
  ctaLabel?: string;
  /** How many packages the sheet offers: one skips its package step. */
  packageCount?: number;
}

export function BookingBar({
  title,
  cityName,
  price,
  isTableFlow = false,
  isStayFlow = false,
  typePriceKey,
  typePriceLabel,
  bookHref,
  thumbnail,
  // observeRef was used by the now-removed desktop sticky bar. Kept in the
  // props so existing call sites don't need to change; intentionally not
  // destructured here.
  slug,
  lang,
  onBookSheet,
  ctaLabel,
  packageCount = 0,
}: BookingBarProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const pewter = useBookingSkin() === "pewter";
  const display = useDisplayPriceParts()(price);
  const [scrolled, setScrolled] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const recentBookingRef = useRecentBooking(slug);
  const draft = useBookingDraft(slug);
  // Show draft variant only when the user hasn't already booked AND has
  // actually filled in something meaningful (past step 0 or any field).
  const hasDraft =
    !recentBookingRef &&
    !!draft &&
    (draft.step > 0 ||
      draft.firstName.trim().length > 0 ||
      draft.lastName.trim().length > 0 ||
      draft.email.trim().length > 0 ||
      draft.phone.replace(/\D/g, "").length > 0);
  // Counted the way the sheet counts (`sheet-steps.ts`): five steps with a
  // package choice, four without, three at a restaurant, each named with the
  // sheet's own title.
  const stepShape = { isTableFlow, packageCount };
  const draftAt = sheetStepPosition(stepShape, draft?.step ?? 0);
  const stepTitles: Record<number, string> = {
    0: t("booking_sheet.step_package_title", { defaultValue: "Choose your package" }),
    // Same wording the sheet's own date step uses, so the draft bar and the
    // step it reopens never disagree.
    1: isStayFlow
      ? t("booking_sheet.step_stay_title", { defaultValue: "When are you staying?" })
      : isTableFlow
        ? t("booking_sheet.step_table_title", { defaultValue: "When and how many?" })
        : t("booking_sheet.step_date_title", { defaultValue: "When are you coming?" }),
    2: t("booking_sheet.step_guests_title", { defaultValue: "Who's joining?" }),
    3: t("booking_sheet.step_details_title", { defaultValue: "Your details" }),
    4: t("booking_sheet.step_review_title", { defaultValue: "Review and confirm" }),
  };
  const draftStepLabels: readonly string[] = sheetStepOrder(stepShape).map((n) => stepTitles[n]!);
  const draftStep = draftAt.current - 1;
  // Poll the live booking status so admin-side confirmations propagate to the
  // bar without a hard reload.
  const liveBooking = useQuery({
    queryKey: bookingKeys.detail(recentBookingRef ?? "", i18n.language),
    queryFn: () => bookingsApi.getById(recentBookingRef!, i18n.language),
    enabled: !!recentBookingRef,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: 0,
    throwOnError: false,
  });
  const liveStatus = liveBooking.data?.status ?? null;
  // "View details" opens a sheet over the page instead of navigating away
  // (owner, 2026-09-10). It renders from `liveBooking`, so it only offers
  // itself once that has actually arrived — before then the control still
  // links to the full page rather than opening an empty sheet.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const isConfirmed =
    liveStatus === "CONFIRMED" || liveStatus === "PAID" || liveStatus === "COMPLETED";
  const isCancelled = liveStatus === "CANCELLED" || liveStatus === "REJECTED";

  /*
   * On the navy strip the ground carried the mood and gold only had to be
   * legible on it, so "waiting" and "done" were the same gold. Pewter's ground
   * is neutral, so the colour has to carry the meaning by itself: gold ink
   * while somebody still owes us an answer, green once the booking is ours,
   * red when it is off. Without that split the three states read alike on pale
   * and "awaiting" stops feeling like waiting.
   */
  const statusInk = pewter
    ? isCancelled
      ? PW.red
      : isConfirmed
        ? PW.green
        : PW.goldDark
    : isCancelled
      ? "#fca5a5"
      : "#E5B265";
  const statusDotBg = pewter
    ? isCancelled
      ? "#B91C1C"
      : isConfirmed
        ? PW.green
        : PW.gold
    : isCancelled
      ? "#dc2626"
      : "#E5B265";
  const statusDotInk = isCancelled || (pewter && isConfirmed) ? "#fff" : "#110E39";

  // Auto-clear the recent booking when it lands a terminal state — no
  // permanent "Awaiting" pill stuck on the page after the user has seen the
  // confirmed/cancelled state once.
  useEffect(() => {
    if (!slug || !recentBookingRef) return;
    if (liveStatus === "COMPLETED" || liveStatus === "CANCELLED" || liveStatus === "REJECTED") {
      clearRecentBooking(slug);
    }
  }, [slug, recentBookingRef, liveStatus]);

  /**
   * A reference the server does not recognise takes the strip down with it.
   *
   * Without this the strip stays on the page forever, and because the live
   * lookup failed it renders the fallback LINK rather than the details sheet —
   * a "View details" that can only ever land on the 404 page. A stored
   * reference that no longer resolves has nothing left to show.
   *
   * Only on a definite refusal: a network blip must not wipe a real booking,
   * so a 404 clears and anything else is left alone to retry.
   */
  useEffect(() => {
    if (!slug || !recentBookingRef) return;
    const error = liveBooking.error as { status?: number } | null;
    if (error?.status === 404) clearRecentBooking(slug);
  }, [slug, recentBookingRef, liveBooking.error]);

  /**
   * What the strip prints. The full `MA-<epoch>-<32-char-hash>` is the DB id
   * behind the unique index and stays the lookup key in `viewBookingHref`
   * below — but at 53 characters it never fit this pill, so the strip was
   * showing a truncated hash fragment that means nothing to the guest and
   * nothing to ops either.
   *
   * `MA-<epoch>` is what every other customer surface already shows — the
   * emails, the thank-you page, the account page, the mobile app, the admin
   * list — and it stays findable (`ilike '%MA-<epoch>%'` matches the row).
   * The strip was the last place leaking the raw id.
   */
  const displayBookingRef = recentBookingRef
    ? shortBookingReference(recentBookingRef) || recentBookingRef
    : null;

  const viewBookingHref = recentBookingRef
    ? `/${lang ?? "en"}/booking/${recentBookingRef}/thank-you`
    : null;
  const whatsappHref = recentBookingRef
    ? `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
        t("activity.whatsapp_message", {
          defaultValue:
            "Hi! I just made a reservation{{titlePart}}. My booking reference is {{reference}}.",
          reference: shortBookingReference(recentBookingRef) || recentBookingRef,
          titlePart: title ? ` for ${title}` : "",
        }),
      )}`
    : null;


  // Auto-cancel a pending discard confirmation if the user doesn't act on it.
  useEffect(() => {
    if (!discardConfirm) return;
    const id = setTimeout(() => setDiscardConfirm(false), 4000);
    return () => clearTimeout(id);
  }, [discardConfirm]);

  // Shrinks the bar a little while the guest scrolls down to read, and brings
  // it back on the way up (owner, 2026-10-06). Same thresholds as the site's
  // bottom tab bar. Written straight to a data attribute so a scroll never
  // re-renders the bar; the CSS animates transform only, which iOS keeps smooth.
  const barRef = useRef<HTMLDivElement>(null);
  // The page's foot leaves this much room below lg (see _layout.tsx).
  useFloatingBarRoom(barRef);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    let lastY = window.scrollY;
    let raf = 0;
    const evaluate = () => {
      raf = 0;
      const y = window.scrollY;
      const dy = y - lastY;
      if (y < 80) delete el.dataset.shrunk;
      else if (dy > 8) el.dataset.shrunk = "";
      else if (dy < -8) delete el.dataset.shrunk;
      if (Math.abs(dy) > 8 || y < 80) lastY = y;
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(evaluate);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Scroll listener — collapses the booked-state bar to a compact pill once
  // the user starts reading the page so it stops covering content.
  // Uses hysteresis (different thresholds for collapse vs expand) + rAF
  // throttling so swapping the bar's height doesn't fight scrollY at the
  // boundary.
  useEffect(() => {
    if (!recentBookingRef) return;
    let raf = 0;
    function evaluate() {
      raf = 0;
      const y = window.scrollY;
      setScrolled((prev) => {
        if (prev) return y > 100;
        return y > 280;
      });
    }
    function onScroll() {
      if (raf) return;
      raf = window.requestAnimationFrame(evaluate);
    }
    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [recentBookingRef]);

  return (
    <>
      {/* Desktop sticky booking bar removed — the right-column
          ActivitySidebar already provides the booking CTA above the
          fold, so a duplicate sticky chrome on top was redundant and
          kept causing layout collisions with the breadcrumb / title.
          /booking-bar-lab kept around for the rejected concepts in
          case we revisit. */}

      <div ref={barRef} data-mj-bookbar className="fixed inset-x-0 bottom-0 z-30 px-3 pb-[max(12px,env(safe-area-inset-bottom))] lg:hidden">
        {recentBookingRef && viewBookingHref && whatsappHref ? (
          <div className="mx-auto max-w-[560px]">
            {/* Compact pill — shown after the user scrolls past the hero */}
            {scrolled ? (
              <div
                className={`relative flex items-center gap-2 rounded-full p-1.5 ps-4 transition-all duration-300${
                  pewter
                    ? ""
                    : " overflow-hidden bg-gradient-to-r from-[#110E39] via-[#1a1554] to-[#241d6e] shadow-[0_-4px_16px_rgba(4,0,51,0.12),0_14px_40px_rgba(17,14,57,0.45)]"
                }`}
                style={
                  pewter
                    ? {
                        ...pewterPanel(28),
                        boxShadow: `${pewterPanel(28).boxShadow}, 0 14px 40px -18px rgba(17,14,57,0.45)`,
                      }
                    : undefined
                }
              >
                <div className="min-w-0 flex-1">
                  <p
                    className="flex items-center gap-1.5 font-sans text-[9.5px] font-bold uppercase tracking-[0.1em]"
                    style={{ color: statusInk }}
                  >
                    <span
                      className="flex h-3 w-3 items-center justify-center rounded-full"
                      style={{ background: statusDotBg }}
                    >
                      <Icon
                        name={
                          isConfirmed
                            ? "ri-check-double-line"
                            : isCancelled
                              ? "ri-close-line"
                              : "ri-check-line"
                        }
                        className="text-[8px]"
                        style={{ color: statusDotInk }}
                        aria-hidden
                      />
                    </span>
                    {isCancelled
                      ? t("activity.rejected", { defaultValue: "Cancelled" })
                      : isConfirmed
                        ? t("activity.confirmed", { defaultValue: "Confirmed" })
                        : t("activity.awaiting_confirmation", {
                            defaultValue: "Awaiting Confirmation",
                          })}
                  </p>
                  <p
                    className={`mt-0.5 [overflow-wrap:anywhere] font-mono text-[11px] font-bold ${
                      pewter ? "text-[#110E39]" : "text-white/85"
                    }`}
                  >
                    {displayBookingRef}
                  </p>
                </div>
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("activity.chat_with_us", { defaultValue: "Chat with us" })}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_8px_18px_rgba(37,211,102,0.4)]"
                >
                  <Icon name="ri-whatsapp-line" className="text-[16px]" aria-hidden />
                </a>
                {liveBooking.data ? (
                  <button
                    type="button"
                    onClick={() => setDetailsOpen(true)}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3.5 py-2.5 font-sans text-[12px] font-bold text-[#110E39]${
                      pewter ? "" : " bg-[#E5B265] shadow-[0_8px_18px_rgba(229,178,101,0.4)]"
                    }`}
                    style={pewter ? CONTROL_REST : undefined}
                  >
                    {t("activity.view_details", { defaultValue: "View details" })}
                    <Icon name={isRtl ? "ri-arrow-left-line text-[13px]" : "ri-arrow-right-line text-[13px]"} aria-hidden />
                  </button>
                ) : (
                  <a
                    href={viewBookingHref}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3.5 py-2.5 font-sans text-[12px] font-bold text-[#110E39]${
                      pewter ? "" : " bg-[#E5B265] shadow-[0_8px_18px_rgba(229,178,101,0.4)]"
                    }`}
                    style={pewter ? CONTROL_REST : undefined}
                  >
                    {t("activity.view_details", { defaultValue: "View details" })}
                    <Icon name={isRtl ? "ri-arrow-left-line text-[13px]" : "ri-arrow-right-line text-[13px]"} aria-hidden />
                  </a>
                )}
              </div>
            ) : (
              <div
                className={`relative rounded-[26px] p-4 transition-all duration-300${
                  pewter
                    ? ""
                    : " overflow-hidden bg-gradient-to-br from-[#110E39] via-[#1a1554] to-[#2a236f] shadow-[0_-6px_24px_rgba(4,0,51,0.12),0_18px_44px_rgba(17,14,57,0.45)]"
                }`}
                style={
                  pewter
                    ? {
                        ...pewterPanel(26),
                        boxShadow: `${pewterPanel(26).boxShadow}, 0 18px 44px -20px rgba(17,14,57,0.45)`,
                      }
                    : undefined
                }
              >
                {/* The gold bloom is the navy strip's own light source. A pewter
                    panel is lit by the band; a bloom under it is just a stain. */}
                {!pewter && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -bottom-16 left-1/2 h-40 w-72 -translate-x-1/2 rounded-full bg-[#E5B265]/20 blur-3xl"
                  />
                )}

                {/* Header — booking ref + status */}
                <div className="relative flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <p
                      className="font-sans text-[10px] font-bold uppercase tracking-[0.12em]"
                      style={{ color: pewter ? PW.muted : "rgba(255,255,255,0.5)" }}
                    >
                      {t("activity.booking", { defaultValue: "Booking" })}
                    </p>
                    <p
                      className={`mt-0.5 [overflow-wrap:anywhere] font-mono text-[12.5px] font-bold ${
                        pewter ? "text-[#110E39]" : "text-white"
                      }`}
                    >
                      {displayBookingRef}
                    </p>
                  </div>
                  <p
                    className="text-end font-sans text-[10.5px] font-bold uppercase tracking-[0.12em]"
                    style={{ color: statusInk }}
                  >
                    {isCancelled
                      ? t("activity.rejected", { defaultValue: "Cancelled" })
                      : isConfirmed
                        ? t("activity.confirmed", { defaultValue: "Confirmed" })
                        : t("activity.awaiting_confirmation", {
                            defaultValue: "Awaiting Confirmation",
                          })}
                  </p>
                </div>

                {/* Timeline */}
                <div className="relative mt-3.5">
                  <div
                    className="absolute inset-x-0 top-[7px] h-0.5"
                    style={{
                      background: pewter ? "rgba(17,14,57,0.10)" : "rgba(255,255,255,0.10)",
                    }}
                  />
                  <div
                    className={`absolute top-[7px] h-0.5 ${
                      isConfirmed ? "w-full" : "w-[55%]"
                    }`}
                    style={{
                      insetInlineStart: 0,
                      /* Still gold while it is running, navy once it has landed:
                         the line's colour has to say the same thing the word
                         beside it says. */
                      backgroundImage: `linear-gradient(${
                        isRtl ? "to left" : "to right"
                      }, ${
                        pewter && isConfirmed
                          ? "rgba(17,14,57,0.35), #110E39"
                          : "rgba(229,178,101,0.5), #E5B265"
                      })`,
                    }}
                  />
                  <div className="relative flex items-start justify-between">
                    <BookingTimelineDot
                      pewter={pewter}
                      label={t("activity.booking_sent", { defaultValue: "Booking sent" })}
                      state="done"
                      align="start"
                    />
                    <BookingTimelineDot
                      pewter={pewter}
                      label={t("activity.timeline_awaiting", { defaultValue: "Awaiting" })}
                      state={isConfirmed ? "done" : "active"}
                      align="center"
                    />
                    <BookingTimelineDot
                      pewter={pewter}
                      label={t("activity.timeline_confirmed", { defaultValue: "Confirmed" })}
                      state={isConfirmed ? "done" : "todo"}
                      align="end"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="relative mt-4 flex items-center gap-2">
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full py-2.5 font-sans text-[12.5px] font-bold text-white"
                    style={WHATSAPP_SOFT}
                  >
                    <Icon name="ri-whatsapp-line" className="text-[15px]" aria-hidden />
                    {t("activity.chat_with_us", { defaultValue: "Chat with us" })}
                  </a>
                  {liveBooking.data ? (
                    <button
                      type="button"
                      onClick={() => setDetailsOpen(true)}
                      className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 font-sans text-[12.5px] font-bold ${
                        pewter
                          ? "text-[#110E39]"
                          : "border border-white/15 bg-white/10 text-white backdrop-blur"
                      }`}
                      style={pewter ? CONTROL_SOFT : undefined}
                    >
                      {t("activity.view_details", { defaultValue: "View details" })}
                      <Icon name={isRtl
                            ? "ri-arrow-left-line text-[14px]"
                            : "ri-arrow-right-line text-[14px]"} aria-hidden />
                    </button>
                  ) : (
                    <a
                      href={viewBookingHref}
                      className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full py-2.5 font-sans text-[12.5px] font-bold ${
                        pewter
                          ? "text-[#110E39]"
                          : "border border-white/15 bg-white/10 text-white backdrop-blur"
                      }`}
                      style={pewter ? CONTROL_SOFT : undefined}
                    >
                      {t("activity.view_details", { defaultValue: "View details" })}
                      <Icon name={isRtl
                            ? "ri-arrow-left-line text-[14px]"
                            : "ri-arrow-right-line text-[14px]"} aria-hidden />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : hasDraft && onBookSheet ? (
          <div className="mx-auto max-w-[560px]">
            {/* The unfinished draft shares this slot with the two strips above
                it, so it converts with them: one navy panel left among pewter
                ones would read as a different product, not as a different
                state. */}
            <div
              className={`relative rounded-[22px] p-3.5${
                pewter
                  ? ""
                  : " overflow-hidden bg-gradient-to-br from-[#110E39] via-[#1a1554] to-[#241d6e] shadow-[0_-4px_16px_rgba(4,0,51,0.12),0_18px_40px_rgba(17,14,57,0.4)]"
              }`}
              style={
                pewter
                  ? {
                      ...pewterPanel(22),
                      boxShadow: `${pewterPanel(22).boxShadow}, 0 18px 40px -18px rgba(17,14,57,0.4)`,
                    }
                  : undefined
              }
            >
              {!pewter && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute -top-12 -right-10 h-32 w-32 rounded-full bg-[#E5B265]/30 blur-2xl"
                />
              )}

              <div className="relative flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p
                      className="flex items-center gap-1.5 font-sans text-[9.5px] font-bold uppercase tracking-[0.12em]"
                      style={{ color: pewter ? PW.goldDark : PW.gold }}
                    >
                      <span className="flex h-3 w-3 items-center justify-center rounded-full bg-[#E5B265]">
                        <Icon name="ri-time-line" className="text-[8px] text-[#110E39]" aria-hidden />
                      </span>
                      {t("activity.in_progress_step", {
                        defaultValue: "In progress · step {{step}} of {{total}}",
                        step: draftAt.current,
                        total: draftAt.total,
                      })}
                    </p>
                    {discardConfirm ? (
                      <span className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setDiscardConfirm(false)}
                          className={`min-h-[24px] rounded-full px-2 py-0.5 font-sans text-[9.5px] font-bold ${
                            pewter ? "text-[#110E39]" : "border border-white/15 bg-white/10 text-white"
                          }`}
                          style={pewter ? CONTROL_REST : undefined}
                        >
                          {t("activity.keep", { defaultValue: "Keep" })}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (slug) clearBookingDraft(slug);
                            setDiscardConfirm(false);
                          }}
                          className={`min-h-[24px] rounded-full px-2 py-0.5 font-sans text-[9.5px] font-bold text-white ${
                            pewter ? "bg-[#9B2C2C]" : "bg-[#dc2626] shadow-[0_4px_10px_rgba(220,38,38,0.45)]"
                          }`}
                        >
                          {t("activity.discard", { defaultValue: "Discard" })}
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDiscardConfirm(true)}
                        aria-label={t("activity.discard_draft", {
                          defaultValue: "Discard draft",
                        })}
                        /* 24×24 minimum hit area (WCAG 2.5.8) — it was 20. */
                        className={`flex h-6 w-6 items-center justify-center rounded-full transition ${
                          pewter
                            ? "bg-[rgba(17,14,57,0.06)] text-[#5a5d77] active:bg-[rgba(17,14,57,0.14)]"
                            : "bg-white/5 text-white/55 active:bg-white/15"
                        }`}
                      >
                        <Icon name="ri-close-line" className="text-[12px]" aria-hidden />
                      </button>
                    )}
                  </div>
                  <p
                    className={`mt-1 break-words font-heading text-[13.5px] font-bold ${
                      pewter ? "text-[#110E39]" : "text-white"
                    }`}
                  >
                    {draftStepLabels[draftStep]}
                  </p>
                  <div className="mt-2 flex items-center gap-1.5">
                    {draftStepLabels.map((_, i) => {
                      const done = i < draftStep;
                      const active = i === draftStep;
                      return (
                        <span
                          key={i}
                          className={`h-1.5 rounded-full transition-all ${
                            done
                              ? `w-3 ${pewter ? "bg-[#110E39]" : "bg-[#E5B265]"}`
                              : active
                                ? "w-6 bg-[#E5B265]"
                                : `w-3 ${pewter ? "bg-[rgba(17,14,57,0.14)]" : "bg-white/15"}`
                          }`}
                        />
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onBookSheet}
                  /* The one thing here you press to move forward, so on pewter
                     it is the chosen control: solid navy, not the band. */
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 font-sans text-[12.5px] font-bold ${
                    pewter
                      ? "text-white"
                      : "bg-[#E5B265] text-[#110E39] shadow-[0_8px_18px_rgba(229,178,101,0.4)]"
                  }`}
                  style={
                    pewter
                      ? { background: PW.navyGradient, boxShadow: PW.primaryShadow }
                      : undefined
                  }
                >
                  {t("activity.continue", { defaultValue: "Continue" })}
                  <Icon name={isRtl
                        ? "ri-arrow-left-line text-[13px]"
                        : "ri-arrow-right-line text-[13px]"} aria-hidden />
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Already white and already round — the band is what was missing.
             It reads a price, so it is a surface, and it gets the frame. */
          <div
            className={`mx-auto flex max-w-[560px] items-center gap-3 rounded-full p-1.5 ps-4${
              pewter
                ? ""
                : " border border-white/60 bg-white/85 shadow-[0_-6px_24px_rgba(4,0,51,0.08),0_14px_40px_rgba(4,0,51,0.12)] backdrop-blur-xl"
            }`}
            style={
              pewter
                ? {
                    ...pewterPanel(30),
                    boxShadow: `${pewterPanel(30).boxShadow}, 0 14px 40px -18px rgba(17,14,57,0.35)`,
                  }
                : undefined
            }
          >
            <div className="min-w-0 flex-1">
              {isTableFlow ? (
                <>
                  <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                    {t("activity.table_reservation", { defaultValue: "Table reservation" })}
                  </p>
                  <p className="font-sans text-[12.5px] font-bold leading-tight text-[#110E39]">
                    {t("activity.table_promise_short", {
                      defaultValue: "Free to reserve",
                    })}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                    {t("activity.starting_from")}
                  </p>
                  <p className="flex items-baseline gap-1 font-heading text-[17px] font-bold leading-none text-[#110E39]">
                    {display.value} <span className="text-[11px]">{display.currency}</span>
                    <span className="font-sans text-[10px] font-medium text-[#5a5d77]">
                      {typePriceLabel ?? t(typePriceKey)}
                    </span>
                  </p>
                </>
              )}
            </div>
            {onBookSheet ? (
              <button
                type="button"
                onClick={onBookSheet}
                className={`flex-shrink-0 rounded-full px-5 py-3 font-sans text-[13px] font-bold text-white${
                  pewter ? "" : " bg-[linear-gradient(180deg,#2E2878_0%,#1A1454_50%,#110E39_100%)] shadow-[0_8px_20px_rgba(17,14,57,0.28),inset_0_1.5px_0_rgba(255,255,255,0.22),inset_0_-1.5px_0_rgba(0,0,0,0.25)]"
                }`}
                style={pewter ? { background: PW.navyGradient, boxShadow: PW.primaryShadow } : undefined}
              >
                {ctaLabel ??
                  (isTableFlow
                    ? t("activity.reserve_a_table", { defaultValue: "Reserve a table" })
                    : t("activity.book_now"))}
              </button>
            ) : (
              <a
                href={bookHref}
                className={`flex-shrink-0 rounded-full px-5 py-3 font-sans text-[13px] font-bold text-white${
                  pewter ? "" : " bg-[linear-gradient(180deg,#2E2878_0%,#1A1454_50%,#110E39_100%)] shadow-[0_8px_20px_rgba(17,14,57,0.28),inset_0_1.5px_0_rgba(255,255,255,0.22),inset_0_-1.5px_0_rgba(0,0,0,0.25)]"
                }`}
                style={pewter ? { background: PW.navyGradient, boxShadow: PW.primaryShadow } : undefined}
              >
                {ctaLabel ??
                  (isTableFlow
                    ? t("activity.reserve_a_table", { defaultValue: "Reserve a table" })
                    : t("activity.book_now"))}
              </a>
            )}
          </div>
        )}
      </div>

      {/* Outside the bar's own fixed wrapper: that one is `lg:hidden` and sits
          at z-30 under the header, which would have clipped the sheet to the
          phone and slid the header over its top edge. */}
      {detailsOpen && liveBooking.data && viewBookingHref && whatsappHref && (
        <BookingDetailsSheet
          booking={liveBooking.data}
          fullDetailsHref={viewBookingHref}
          whatsappHref={whatsappHref}
          onClose={() => setDetailsOpen(false)}
          onCancelled={() => {
            // The strip exists because this device has a live booking on this
            // activity. Once it is cancelled it does not, so the strip goes
            // with it rather than sitting there advertising a dead reference.
            if (slug) clearRecentBooking(slug);
          }}
        />
      )}
    </>
  );
}

function BookingTimelineDot({
  label,
  state,
  align,
  pewter = false,
}: {
  label: string;
  state: "done" | "active" | "todo";
  align: "start" | "center" | "end";
  pewter?: boolean;
}) {
  /*
   * On navy, done and active were both gold and the difference was the check
   * versus the pulse. On pale that reads as two identical dots, so the step
   * still being waited on is the ONLY gold one: settled steps go navy, the
   * one in flight stays gold and keeps pulsing, and what has not happened yet
   * is an empty white ring. Three states, three looks, still one direction.
   */
  const dot = pewter
    ? state === "done"
      ? "bg-[#110E39] ring-[rgba(17,14,57,0.14)]"
      : state === "active"
        ? "bg-[#E5B265] ring-[rgba(229,178,101,0.35)]"
        : "bg-white ring-[rgba(17,14,57,0.14)]"
    : state === "done"
      ? "bg-[#E5B265] ring-[#E5B265]/25"
      : state === "active"
        ? "bg-[#E5B265] ring-[#E5B265]/40"
        : "bg-white/15 ring-transparent";

  return (
    <div
      className={`flex flex-col gap-1 ${
        align === "center" ? "items-center" : align === "end" ? "items-end" : "items-start"
      }`}
    >
      <span className={`flex h-[14px] w-[14px] items-center justify-center rounded-full ring-2 ${dot}`}>
        {state === "done" && (
          <Icon
            name="ri-check-line"
            className={`text-[8px] ${pewter ? "text-white" : "text-[#110E39]"}`}
            aria-hidden
          />
        )}
        {state === "active" && (
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#110E39]" />
        )}
      </span>
      <span
        className="font-sans text-[9.5px] font-bold uppercase tracking-[0.06em]"
        style={{
          color: pewter
            ? state === "todo"
              ? PW.muted
              : PW.navy
            : state === "todo"
              ? "rgba(255,255,255,0.45)"
              : "rgba(255,255,255,0.85)",
        }}
      >
        {label}
      </span>
    </div>
  );
}
